import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-font-family";
import Image from "@tiptap/extension-image";
import FontSize from "@/lib/tiptap/FontSize";
import { cn } from "@/lib/utils";
import "./RichTextEditor.css";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { uploadFileToFirebase } from "@/lib/firebase/mediaUploading";

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  onReady?: (editor: unknown) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  simpleToolbar?: boolean;
  defaultTextColor?: string;
  backgroundColor?: string;
  showImageAndColor?: boolean;
  defaultFontSize?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = "150px",
  maxHeight,
  onBlur,
  onFocus,
  simpleToolbar = false,
  defaultTextColor,
  backgroundColor,
  showImageAndColor = false,
  defaultFontSize = "16",
}: RichTextEditorProps) {
  const [fontSize, setFontSize] = React.useState(defaultFontSize);
  const [fontFamily, setFontFamily] = React.useState("Pangram");
  const [showImagePanel, setShowImagePanel] = React.useState(false);
  const [imageTab, setImageTab] = React.useState<"upload" | "url">("upload");
  const [imageUrl, setImageUrl] = React.useState("");
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextStyle,
      Color,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      FontSize,
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onCreate: ({ editor }) => {
      const hasContent = editor.state.doc.textContent.trim().length > 0;
      if (hasContent) {
        // Existing content: apply defaults to all text so the color picker reflects correctly
        const chain = editor.chain().selectAll().setFontFamily("Pangram").setFontSize(`${defaultFontSize}px`);
        if (defaultTextColor) chain.setColor(defaultTextColor);
        chain.focus('end').run();
      } else {
        // Empty editor: set stored marks so new typing uses defaults.
        // All attributes must be in ONE setMark call — multiple chained calls each
        // call addStoredMark which replaces the previous textStyle mark entirely,
        // so only the last attribute would survive.
        editor.chain().setMark('textStyle', {
          fontFamily: "Pangram",
          fontSize: `${defaultFontSize}px`,
          ...(defaultTextColor ? { color: defaultTextColor } : {}),
        }).run();
      }
    },
    onBlur: () => onBlur?.(),
    onFocus: ({ editor }) => {
      // Re-apply defaults whenever focus returns and no explicit color is active at cursor.
      // Must set all three in one setMark call — separate calls overwrite each other.
      if (defaultTextColor && !editor.getAttributes('textStyle').color) {
        editor.chain().setMark('textStyle', {
          fontFamily: "Pangram",
          fontSize: `${defaultFontSize}px`,
          color: defaultTextColor,
        }).run();
      }
      onFocus?.();
    },
    editorProps: {
      attributes: {
        class: "focus:outline-none p-3",
        style: `min-height: ${minHeight}; ${maxHeight ? `max-height: ${maxHeight};` : ""} overflow-y: auto; font-family: Pangram, sans-serif;${defaultTextColor ? ` color: ${defaultTextColor};` : ""}${backgroundColor ? ` background-color: ${backgroundColor};` : ""}`,

      },
    },
  });

  // Sync external value changes into the editor
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const insertImage = React.useCallback((url: string) => {
    if (!url) return;
    editor?.chain().focus().setImage({ src: url }).run();
    setShowImagePanel(false);
    setImageUrl("");
  }, [editor]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFileToFirebase(file, "cards");
      insertImage(url);
    } catch {
      // silently ignore upload errors — user can try URL tab instead
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("border border-input rounded-md shadow-xs overflow-hidden rich-text-editor", className)}>
      <div className="border-b bg-muted/30 p-2 flex flex-wrap gap-1 items-center">
        <Select
          value={fontFamily}
          onValueChange={(value) => {
            setFontFamily(value);
            editor.chain().focus().setFontFamily(value).run();
          }}
        >
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue placeholder="Font" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Pangram">Pangram</SelectItem>
            <SelectItem value="sans-serif">Normal</SelectItem>
            <SelectItem value="Courier New">Courier New</SelectItem>
            <SelectItem value="cursive">Cursive</SelectItem>
            <SelectItem value="monospace">Monospace</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={fontSize}
          onValueChange={(value) => {
            setFontSize(value);
            editor.chain().focus().setFontSize(`${value}px`).run();
          }}
        >
          <SelectTrigger className="w-[80px] h-8 text-xs">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="12">12px</SelectItem>
            <SelectItem value="14">14px</SelectItem>
            <SelectItem value="16">16px</SelectItem>
            <SelectItem value="18">18px</SelectItem>
            <SelectItem value="20">20px</SelectItem>
            <SelectItem value="24">24px</SelectItem>
            <SelectItem value="28">28px</SelectItem>
            <SelectItem value="32">32px</SelectItem>
          </SelectContent>
        </Select>
        {!simpleToolbar && (
          <>
            <div className="border-l mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={cn(
                "px-2 py-1 rounded text-sm font-medium hover:bg-muted",
                editor.isActive("heading", { level: 1 }) ? "bg-muted" : ""
              )}
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={cn(
                "px-2 py-1 rounded text-sm font-medium hover:bg-muted",
                editor.isActive("heading", { level: 2 }) ? "bg-muted" : ""
              )}
            >
              H2
            </button>
            <div className="border-l mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn(
                "px-2 py-1 rounded text-sm font-medium hover:bg-muted",
                editor.isActive("bold") ? "bg-muted" : ""
              )}
            >
              B
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn(
                "px-2 py-1 rounded text-sm italic hover:bg-muted",
                editor.isActive("italic") ? "bg-muted" : ""
              )}
            >
              I
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={cn(
                "px-2 py-1 rounded text-sm underline hover:bg-muted",
                editor.isActive("underline") ? "bg-muted" : ""
              )}
            >
              U
            </button>
            <div className="border-l mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={cn(
                "px-2 py-1 rounded text-sm hover:bg-muted",
                editor.isActive("bulletList") ? "bg-muted" : ""
              )}
            >
              • List
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={cn(
                "px-2 py-1 rounded text-sm hover:bg-muted",
                editor.isActive("orderedList") ? "bg-muted" : ""
              )}
            >
              1. List
            </button>
            <div className="border-l mx-1" />
            {showImageAndColor && (
              <>
                <label
                  className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted cursor-pointer text-sm"
                  title="Text color"
                >
                  <span
                    style={{ borderBottom: `3px solid ${editor.getAttributes('textStyle').color ?? defaultTextColor ?? '#000000'}` }}
                    className="font-bold"
                  >
                    A
                  </span>
                  <input
                    type="color"
                    className="w-0 h-0 opacity-0 absolute"
                    value={editor.getAttributes('textStyle').color ?? defaultTextColor ?? '#000000'}
                    onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
                  />
                </label>
                <div className="border-l mx-1" />
                {showImagePanel ? (
              <div className="flex flex-col gap-2 border border-input rounded-md p-2 bg-background shadow-md z-10">
                {/* Tabs */}
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setImageTab("upload")}
                    className={cn("px-3 py-1 rounded text-xs font-medium", imageTab === "upload" ? "bg-primary text-primary-foreground" : "hover:bg-muted")}
                  >
                    Upload
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageTab("url")}
                    className={cn("px-3 py-1 rounded text-xs font-medium", imageTab === "url" ? "bg-primary text-primary-foreground" : "hover:bg-muted")}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    className="ml-auto px-2 py-1 rounded text-xs hover:bg-muted"
                    onClick={() => { setShowImagePanel(false); setImageUrl(""); }}
                  >
                    ✕
                  </button>
                </div>
                {imageTab === "upload" ? (
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      className="px-3 py-1 rounded text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                      {uploading ? "Uploading…" : "Choose image"}
                    </button>
                    <span className="text-xs text-muted-foreground">jpg, png, gif, webp</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <input
                      type="url"
                      autoFocus
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.png"
                      className="h-8 text-xs border border-input rounded px-2 w-56 bg-background text-foreground"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") insertImage(imageUrl);
                        if (e.key === "Escape") { setShowImagePanel(false); setImageUrl(""); }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => insertImage(imageUrl)}
                      className="px-2 py-1 rounded text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Insert
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                title="Insert image"
                className="px-2 py-1 rounded text-sm hover:bg-muted"
                onClick={() => setShowImagePanel(true)}
              >
                🖼 Img
              </button>
            )}
              </>
            )}
          </>
        )}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export default RichTextEditor;
